// ============================================================================
// PRODUCT SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { productEndpoints, PRODUCT_BASE_PATH } from '../endpoints/product.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  Product,
  ProductListItem,
  CreateProductRequest,
  UpdateProductRequest,
  ProductSearchRequest,
} from '../types/product.types';

class ProductService {
  async getAll(params?: ProductSearchRequest): Promise<PaginatedResponse<ProductListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(PRODUCT_BASE_PATH, params)
      : productEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Product>> {
    const response = await get<ApiResponse<Product>>(productEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<Product>> {
    const response = await get<ApiResponse<Product>>(productEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateProductRequest): Promise<ApiResponse<Product>> {
    const response = await post<ApiResponse<Product>>(productEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateProductRequest): Promise<ApiResponse<Product>> {
    const response = await put<ApiResponse<Product>>(productEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(productEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${productEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : productEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const productService = new ProductService();
export default productService;
